import React from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import type { UploadStatus } from '@/hooks/submission_forms/useUploadProgress';

interface UploadProgressIndicatorProps {
  status: UploadStatus;
  /** Position in a multi-file batch; the counter only shows for 2+ files. */
  batch?: { current: number; total: number } | null;
}

/**
 * OV-1380. Shown under a file field while an upload is in flight, so a large
 * file visibly "doing something" explains why its section isn't complete yet.
 * Renders nothing once the upload has finished or failed.
 */
export const UploadProgressIndicator: React.FC<
  UploadProgressIndicatorProps
> = ({ status, batch }) => {
  if (status?.phase !== 'uploading' && status?.phase !== 'finishing') {
    return null;
  }

  const finishing = status.phase === 'finishing';
  const percent = finishing ? 100 : status.percent;

  return (
    <div
      role="status"
      aria-live="polite"
      className="mt-2 space-y-1 print:hidden"
    >
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="truncate">
          {batch && batch.total > 1 && (
            <span className="text-muted-foreground">
              File {batch.current} of {batch.total} ·{' '}
            </span>
          )}
          {status.fileName}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
          {finishing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Finishing upload…
            </>
          ) : (
            `Uploading… ${percent}%`
          )}
        </span>
      </div>
      <Progress
        value={percent}
        aria-label={`Upload progress for ${status.fileName}`}
        className={finishing ? 'animate-pulse' : undefined}
      />
    </div>
  );
};

/** The "done" marker placed beside a file that was just uploaded. */
export const UploadCompleteBadge: React.FC = () => (
  <span
    role="status"
    className="flex items-center gap-1 text-sm text-success-600 print:hidden"
  >
    <CheckCircle2 className="h-4 w-4" aria-hidden />
    Upload complete
  </span>
);
